import type {
    AdvanceProvisionList,
    AdvanceProvisionListParams,
    AdvanceProvisionRepository,
    AdvanceProvisionRow,
    AdvanceProvisionRule,
    AdvanceProvisionRules,
    AdvanceProvisionRulesResponse,
    AdvanceProvisionRuleType,
} from ".";

const slice = (startMonth: number, endMonth: number | null, rate: number): AdvanceProvisionRule => ({
    label: null,
    conditionType: null,
    startMonth,
    endMonth,
    rate,
    isActive: true,
});

export const advanceProvisionDefaultsFixture: AdvanceProvisionRules = {
    unpublished: [
        slice(0, 3, 0),
        slice(4, 12, 5),
        slice(13, 24, 30),
        slice(25, 36, 50),
        slice(37, 48, 70),
        slice(49, 60, 80),
        slice(61, null, 100),
    ],
    published: [slice(0, 3, 0), slice(4, 12, 60), slice(13, 24, 80), slice(25, null, 100)],
    override: [{ ...slice(0, null, 100), label: "Contrats terminés", conditionType: "term_ended" }],
};

export const advanceProvisionRowsFixture: AdvanceProvisionRow[] = [
    {
        termId: 1,
        beneficiaryName: "MARTIN Jeanne",
        workName: "Le Grand Voyage",
        workCode: "OE-0042",
        termReference: "C-2023-118",
        compensationGroupName: null,
        status: "unpublished",
        contractualAdvance: 1_000_000,
        paidAdvance: 500_000,
        recoupedRoyalties: 0,
        unrecoupedBalance: 500_000,
        firstPublicationDate: null,
        referenceDate: "2024-03-15",
        elapsedMonths: 15,
        slice: { startMonth: 13, endMonth: 24 },
        rate: 30,
        provisionAmount: 150_000,
        overrideLabel: null,
    },
    {
        termId: 2,
        beneficiaryName: "DURAND Paul",
        workName: "Nuits blanches",
        workCode: "OE-0107",
        termReference: "C-2021-034",
        compensationGroupName: "Durand – séries",
        status: "ended",
        contractualAdvance: 300_000,
        paidAdvance: 300_000,
        recoupedRoyalties: 100_000,
        unrecoupedBalance: 200_000,
        firstPublicationDate: "2022-02-01",
        referenceDate: "2022-02-01",
        elapsedMonths: 40,
        slice: null,
        rate: 100,
        provisionAmount: 200_000,
        overrideLabel: "Contrats terminés",
    },
];

export class AdvanceProvisionInMem implements AdvanceProvisionRepository {
    private rules: AdvanceProvisionRules | null = null;
    exportedClosingDates: string[] = [];

    constructor(private rows: AdvanceProvisionRow[] = advanceProvisionRowsFixture) {}

    async fetchRules(): Promise<AdvanceProvisionRulesResponse> {
        return { rules: this.rules ?? advanceProvisionDefaultsFixture, defaults: advanceProvisionDefaultsFixture };
    }

    async saveRules(
        ruleType: AdvanceProvisionRuleType,
        rules: AdvanceProvisionRule[],
    ): Promise<AdvanceProvisionRule[]> {
        this.rules = { ...(this.rules ?? advanceProvisionDefaultsFixture), [ruleType]: rules };
        return rules;
    }

    async fetchList({
        closingDate,
        page,
        pageSize,
        sort,
        order,
    }: AdvanceProvisionListParams): Promise<AdvanceProvisionList> {
        const direction = order === "desc" ? -1 : 1;
        const sorted = [...this.rows].sort((a, b) => (a[sort]! > b[sort]! ? direction : -direction));
        const sum = (key: "contractualAdvance" | "paidAdvance" | "unrecoupedBalance" | "provisionAmount") =>
            this.rows.reduce((total, row) => total + row[key], 0);
        return {
            closingDate,
            total: this.rows.length,
            rows: sorted.slice((page - 1) * pageSize, page * pageSize),
            summary: {
                termCount: this.rows.length,
                contractualAdvance: sum("contractualAdvance"),
                paidAdvance: sum("paidAdvance"),
                unrecoupedBalance: sum("unrecoupedBalance"),
                provisionAmount: sum("provisionAmount"),
            },
        };
    }

    async exportList(closingDate: string): Promise<{ url: string }> {
        this.exportedClosingDates.push(closingDate);
        return { url: `https://files.test/provision_a-valoir_${closingDate}.xlsx` };
    }
}
